import { AuthContext, AuthContextProps } from "components/AuthProvider";
import {
  GET_PATIENT_BY_MOBILE_NUMBER,
  GET_PRIMARY_PATIENTS_BY_MOBILE_NUMBER,
} from "graphql/profiles";
import {
  GetPatientByMobileNumber,
  GetPatientByMobileNumberVariables,
} from "graphql/types/GetPatientByMobileNumber";
import { useContext, useEffect, useState } from "react";
import _find from "lodash/find";
import { isBrowser } from "helpers/commonHelpers";
import { useQueryWithSkip } from "./apolloHooks";
import { SignedinState } from "components/enums/SignedinState";
import {
  GetPrimaryPatientsByMobileNumber,
  GetPrimaryPatientsByMobileNumberVariables,
} from "graphql/types/GetPrimaryPatientsByMobileNumber";

const useAuthContext = () => useContext<AuthContextProps>(AuthContext);
export const useAuth = () => {
  // const verifyOtp = useAuthContext().verifyOtp!;
  // const verifyOtpError = useAuthContext().verifyOtpError!;
  // const setVerifyOtpError = useAuthContext().setVerifyOtpError!;
  // const isVerifyingOtp = useAuthContext().isVerifyingOtp!;

  // const sendOtp = useAuthContext().sendOtp!;
  // const sendEmailOtp = useAuthContext().sendEmailOtp;
  // const sendOtpOnCall = useAuthContext().sendOtpOnCall!;
  // const resendOtp = useAuthContext().resendOtp!;
  // const sendOtpError = useAuthContext().sendOtpError;
  // const isSendingOtp = useAuthContext().isSendingOtp;

  const authContext = useAuthContext();

  const hasAuthToken = authContext.hasAuthToken;
  const authToken = authContext.authToken;
  const isSigningIn = authContext.isSigningIn;
  const signInError = authContext.signInError;
  const currentPatient = authContext.currentPatient;
  const allCurrentPatients = authContext.allCurrentPatients;
  const setCurrentPatientId = authContext.setCurrentPatientId;
  const isSignedIn = authContext.signedin;
  const isUserLoginResolved =
    isSignedIn === SignedinState.SIGNEDIN ||
    isSignedIn === SignedinState.SIGNEDOUT;
  const signOut = authContext.signOut!;
  const customLoginId = authContext.customLoginId;

  const isConsentFormVisible = authContext.isConsentFormVisible;
  const isLoading = authContext.isLoading;
  const setIsLoading = authContext.setIsLoading;
  const currentPatientId = authContext.currentPatientId;
  const verifyExternalTokenAndLogin = authContext.verifyExternalTokenAndLogin;
  const setIsSigningIn = authContext.setIsSigningIn;
  const setCustomLoginId = authContext.setCustomLoginId;
  const app = authContext.app;
  const wait = authContext.wait;
  const setFirebaseUser = authContext.setFirebaseUser;
  const setSSOToken = authContext.setSSOToken;

  const setIsConsentFormVisible = authContext.setIsConsentFormVisible;
  const setAuthToken = authContext.setAuthToken;
  const firebaseUser = authContext.firebaseUser;
  const setIsSignedIn = authContext.setSignedin;
  const setCurrentPatient = authContext.setCurrentPatient;
  const { isPartnerLogin, setIsPartnerLogin } = authContext;
  const setAllCurrentPatients = authContext.setAllCurrentPatients;
  const sectionName = authContext.sectionName;
  const lob = authContext.lob;

  useEffect(() => {
    const patient = allCurrentPatients?.length
      ? allCurrentPatients.find((patient) => patient?.id === currentPatientId)
      : null;
    // Update current patient only if patient found from patientId
    if (patient) {
      setCurrentPatient(patient);
    }
  }, [currentPatientId, allCurrentPatients]);

  return {
    // verifyOtp,
    // verifyOtpError,
    // setVerifyOtpError,
    // isVerifyingOtp,
    // sendOtp,
    // sendEmailOtp,
    currentPatientId,
    // sendOtpOnCall,
    // resendOtp,
    // sendOtpError,
    // isSendingOtp,
    hasAuthToken,
    authToken,
    isSigningIn,
    signInError,
    isSignedIn,
    signOut,
    customLoginId,
    isLoading,
    isConsentFormVisible,
    setIsConsentFormVisible,
    setIsLoading,
    verifyExternalTokenAndLogin,
    setIsSigningIn,
    setCustomLoginId,
    app,
    wait,
    firebaseUser,
    setFirebaseUser,
    setSSOToken,
    setAuthToken,
    currentPatient,
    allCurrentPatients,
    setCurrentPatientId,
    isPartnerLogin,
    setIsPartnerLogin,
    setIsSignedIn,
    setCurrentPatient,
    setAllCurrentPatients,
    isUserLoginResolved,
    isUserCircle: authContext.isUserCircle,
    primaryPatients: authContext.primaryPatients ?? [],
    sectionName,
    lob,
  };
};

export const useCurrentPatient = () => useAuthContext()?.currentPatient;

export const useLoginPopupState = (): {
  isLoginPopupVisible: AuthContextProps["isLoginPopupVisible"];
  setIsLoginPopupVisible: NonNullable<
    AuthContextProps["setIsLoginPopupVisible"]
  >;
} => {
  const isLoginPopupVisible = useAuthContext().isLoginPopupVisible;
  const setIsLoginPopupVisible = useAuthContext().setIsLoginPopupVisible!;
  return {
    isLoginPopupVisible,
    setIsLoginPopupVisible,
  };
};

export const useRefetchPatients = () => {
  const [isRefetchTriggered, setIsRefetchTriggered] = useState<boolean>(false);

  if (isBrowser()) {
    const {
      loading,
      data,
      error,
      refetch: refetchAllPatients,
    } = useQueryWithSkip<
      GetPatientByMobileNumber,
      GetPatientByMobileNumberVariables
    >(GET_PATIENT_BY_MOBILE_NUMBER, {
      variables: {
        mobileNumber: localStorage.getItem("mobile_num"),
      },
    });

    const {
      loading: primaryLoading,
      data: response,
      error: primaryError,
      refetch: refetchPrimaryPatients,
    } = useQueryWithSkip<
      GetPrimaryPatientsByMobileNumber,
      GetPrimaryPatientsByMobileNumberVariables
    >(GET_PRIMARY_PATIENTS_BY_MOBILE_NUMBER, {
      variables: {
        mobileNumber: localStorage.getItem("mobile_num"),
      },
    });

    const refetchPatients = () => {
      Promise.all([refetchAllPatients(), refetchPrimaryPatients()])
        .then(() => {
          setIsRefetchTriggered(true);
        })
        .catch((err) => {
          console.error("Error fetch patient by mobile number", err);
        });
    };

    if (isRefetchTriggered) {
      if (data?.getPatientByMobileNumber?.patients?.length) {
        const setAllCurrentPatients = useAuthContext().setAllCurrentPatients!;
        const currentPatients = data?.getPatientByMobileNumber?.patients;
        setAllCurrentPatients(currentPatients);
      }
      if (response?.getPrimaryPatientsByMobileNumber?.patients?.length) {
        const setPrimaryPatients = useAuthContext().setPrimaryPatients;
        const primaryPatients =
          response?.getPrimaryPatientsByMobileNumber?.patients;
        setPrimaryPatients(primaryPatients);
      }
      setIsRefetchTriggered(false);
    }

    return {
      loading: loading || primaryLoading,
      error: error || primaryError,
      refetchPatients,
    };
  } else {
    return {};
  }
};
